import { useEffect, useMemo, useState } from 'react';
import axiosInstant from '../api';
import { getProjectExpertProductScores } from '../api/endpoints';
import { ExpertTypeEntry } from './useProjectExpertScores';

export interface SearchScoreProduct {
    desireid: number;
    title: string;
}

export interface SearchExpertScoresResponse {
    products: SearchScoreProduct[];
    /** Keyed by userId → { desireid: score in [0,1] }. */
    scoresByUserId: Record<string, Record<string, number>>;
    /** Keyed by userId → { desireid: Industry/Use-Case classification }. */
    expertTypesByUserId: Record<string, Record<string, ExpertTypeEntry>>;
    /** Keyed by userId → true when Industry for ≥1 project product. */
    isIndustryByUserId: Record<string, boolean>;
}

const EMPTY: SearchExpertScoresResponse = {
    products: [],
    scoresByUserId: {},
    expertTypesByUserId: {},
    isIndustryByUserId: {},
};

/**
 * Fetches per-(expert, product) algorithm scores for the Expert Proposals SEARCH
 * pool — experts you can add to a project who aren't yet on it (so the project
 * expert-scores endpoint doesn't cover them). Pass the visible search-result
 * user ids; the hook re-fetches when that set changes.
 *
 * Cheap + indexed on the backend, but we cap the id list and dedupe so a noisy
 * paginating list doesn't spam requests.
 */
export function useSearchExpertScores(
    projectId: number | undefined,
    userIds: number[],
    { skip = false }: { skip?: boolean } = {}
): SearchExpertScoresResponse {
    const [state, setState] = useState<SearchExpertScoresResponse>(EMPTY);

    // Stable key so the effect only fires when the actual id set changes.
    const idsKey = useMemo(
        () => Array.from(new Set(userIds.filter(Boolean))).sort((a, b) => a - b).join(','),
        [userIds]
    );

    useEffect(() => {
        if (skip || !projectId || idsKey === '') {
            setState(EMPTY);
            return;
        }
        let cancelled = false;
        axiosInstant
            .get(getProjectExpertProductScores(projectId, idsKey))
            .then((res) => {
                if (cancelled) return;
                setState({
                    products: res.data?.products || [],
                    scoresByUserId: res.data?.scoresByUserId || {},
                    expertTypesByUserId: res.data?.expertTypesByUserId || {},
                    isIndustryByUserId: res.data?.isIndustryByUserId || {},
                });
            })
            .catch(() => {
                if (!cancelled) setState(EMPTY);
            });
        // eslint-disable-next-line consistent-return
        return () => { cancelled = true; };
    }, [projectId, idsKey, skip]);

    return state;
}
